import {AfterViewInit, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {SharedService} from '../api/shared-service';
import {ChapterApiService} from '../api/chapter-api.service';
import {NotificationApiService} from '../api/notification-api.service';
import {EventService, PublicEventSummaryDTO} from '../api/event-api.service';
import {SpinnerService} from '../shared/services/spinner.service';
import {finalize} from 'rxjs';
import {NavigationEnd, Router} from '@angular/router';
import {ChapterNotification} from '../models/notification.model';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.css'],  // Corrected to 'styleUrls' instead of 'styleUrl'
})
export class HeaderComponent implements OnInit, AfterViewInit, OnDestroy {
    username: any;
    userRole: any;
    chapterList: any;
    necChapter: any;
    notificationCount: number = 0;
    notifications: ChapterNotification[] = [];
    showNotificationPopup: boolean = false;
    private notificationAutoCloseTimer: any;
    necOpenEvents: PublicEventSummaryDTO[] = [];

    @ViewChild('navbarCollapse') navbarCollapse!: ElementRef;

    constructor(
        private sharedService: SharedService,
        private chapterApiService: ChapterApiService,
        private notificationApiService: NotificationApiService,
        private eventApiService: EventService,
        private spinner: SpinnerService,
        private router: Router
    ) {
        this.username = sessionStorage.getItem('username');
        this.userRole = JSON.parse(sessionStorage.getItem('rolesPermission') || '{}');
        this.spinner.show(); // Show spinner while loading chapters

        this.chapterApiService.getData().pipe(
            finalize(() => this.spinner.hide()) // ✅ Hide spinner after outermost call completes
        ).subscribe(
            (response) => {
                this.chapterList = response;

                // Find the chapter with id = 0
                const nec = response.find((chapter: any) => chapter.id === "0");

                if (nec) {
                    this.necChapter = nec;
                }

                // Filter out the chapter with id = 0 from the main list
                this.chapterList = response.filter((chapter: any) => chapter.id !== "0");
            },
            (error) => {
                console.error('Failed to load chapters', error);
            }
        );

        this.loadNecOpenEvents();
    }

    loadNecOpenEvents(): void {
        this.eventApiService.getPublicOpenNecEvents().subscribe({
            next: (events) => this.necOpenEvents = events || [],
            error: () => this.necOpenEvents = []
        });
    }

    isDivVisible: boolean = false;

    // Drives the compact/elevated header state once the page scrolls — the legacy template
    // shipped CSS for this (.cbp-af-header-shrink) but never actually wired up a scroll listener
    // anywhere, so it was permanently dead. This is the real thing.
    scrolled: boolean = false;

    @HostListener('window:scroll')
    onWindowScroll(): void {
        this.scrolled = window.scrollY > 24;
    }

    ngOnInit(): void {
        this.username = sessionStorage.getItem('username');
        this.userRole = JSON.parse(sessionStorage.getItem('rolesPermission') || '{}');

        this.sharedService.userRole$.subscribe((role) => {
            if (typeof role === 'string') {
                this.userRole = JSON.parse(role || '{}');
            } else {
                this.userRole = role;
            }
        });

        this.sharedService.divVisibility$.subscribe((isVisible) => {
            this.isDivVisible = isVisible;
        });

        this.loadNotificationCount();
    }

    loadNotificationCount(): void {
        const userId = sessionStorage.getItem('userId');
        if (userId) {
            this.notificationApiService.getForUser(userId).subscribe({
                next: (data) => {
                    this.notifications = data;
                    this.notificationCount = data.length;
                },
                error: () => {
                    this.notifications = [];
                    this.notificationCount = 0;
                }
            });
        }
    }

    // Bell click opens a popup instead of navigating straight to /profile; it closes itself
    // after a few seconds, or immediately if the person clicks elsewhere.
    toggleNotificationPopup(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
        this.showNotificationPopup = !this.showNotificationPopup;
        if (this.showNotificationPopup) {
            this.scheduleNotificationAutoClose();
        } else {
            clearTimeout(this.notificationAutoCloseTimer);
        }
    }

    private scheduleNotificationAutoClose(): void {
        clearTimeout(this.notificationAutoCloseTimer);
        this.notificationAutoCloseTimer = setTimeout(() => {
            this.showNotificationPopup = false;
        }, 6000);
    }

    @HostListener('document:click')
    closeNotificationPopup(): void {
        if (this.showNotificationPopup) {
            this.showNotificationPopup = false;
            clearTimeout(this.notificationAutoCloseTimer);
        }
    }

    ngAfterViewInit(): void {
        // Close navbar on route change
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd && this.navbarCollapse) {
                const navbar = this.navbarCollapse.nativeElement;
                if (navbar.classList.contains('show')) {
                    navbar.classList.remove('show'); // Close navbar if it's open
                }
            }
        });
    }

    ngOnDestroy(): void {
        clearTimeout(this.notificationAutoCloseTimer);
    }
}
